<app-page-header [title]="'Physical Samples'" [subtitle]="'Manage physical samples for SampleStudio'"></app-page-header>

@if (physicalSampleDiscoveries.length > 0) {
    <div class="mb-6 border border-amber-300 dark:border-amber-700 rounded-sm overflow-hidden" isProducer>
        @if (discoveriesHidden) {
            <div class="flex items-center justify-between gap-4 p-4 bg-amber-50 dark:bg-amber-950/30">
                <span class="text-sm">
                    {{ physicalSampleDiscoveries.length }} discovered physical sample(s) hidden.
                </span>
                <p-button
                        (click)="toggleDiscoveriesVisibility()"
                        icon="pi pi-eye"
                        label="Show"
                        severity="secondary"
                        text
                ></p-button>
            </div>
        } @else {
            <div class="flex items-center justify-between gap-4 p-4 bg-amber-50 dark:bg-amber-950/30">
                <div>
                    <h2 class="font-semibold">Discovered Physical Samples</h2>
                    <p class="text-sm text-gray-600 dark:text-gray-300">
                        Assign a digital sample and confirm devices discovered by a Studio Hub.
                    </p>
                </div>
                <div class="flex gap-2">
                    <p-button
                            (click)="loadPhysicalSampleDiscoveries()"
                            [loading]="loadingDiscoveries"
                            icon="pi pi-refresh"
                            pTooltip="Refresh discoveries"
                            severity="secondary"
                            text
                    ></p-button>
                    <p-button
                            (click)="toggleDiscoveriesVisibility()"
                            icon="pi pi-eye-slash"
                            pTooltip="Hide this panel"
                            severity="secondary"
                            text
                    ></p-button>
                </div>
            </div>

            <p-table
                    [loading]="loadingDiscoveries"
                    [paginator]="true"
                    [rowsPerPageOptions]="[10, 25, 50]"
                    [rows]="10"
                    [value]="physicalSampleDiscoveries"
                    dataKey="id"
                    paginatorDropdownAppendTo="body"
            >
                <ng-template #header>
                    <tr>
                        <th>MAC Address</th>
                        <th>Detected Studio</th>
                        <th>Last Seen</th>
                        <th>Hardware</th>
                        <th>Sample</th>
                        <th>Studio</th>
                        <th></th>
                    </tr>
                </ng-template>
                <ng-template #body let-discovery>
                    <tr>
                        <td>
                            <div class="flex items-center gap-2">
                                <p-tag
                                        [severity]="discovery.isOnline ? 'success' : 'secondary'"
                                        [value]="discovery.isOnline ? 'Online' : 'Offline'"
                                ></p-tag>
                                <span class="font-mono">{{ discovery.macAddress }}</span>
                            </div>
                        </td>
                        <td>{{ discovery.studioName }}</td>
                        <td>{{ discovery.lastSeenAt | date:'medium' }}</td>
                        <td>
                            <div class="flex flex-col text-sm">
                                <span>PCB: {{ discovery.pcb || 'Unknown' }}</span>
                                <span>Firmware: {{ discovery.firmwareVersion || 'Unknown' }}</span>
                            </div>
                        </td>
                        <td class="min-w-64">
                            <p-select
                                    [(ngModel)]="discovery.selectedSampleId"
                                    [options]="sampleOptions"
                                    [panelStyle]="{ 'width': '100%' }"
                                    appendTo="body"
                                    class="w-full"
                                    filter
                                    optionLabel="printedName"
                                    optionValue="id"
                                    placeholder="Select Sample"
                            >
                                <ng-template #selectedItem let-sample>
                                    @if (sample) {
                                        <div class="flex items-center gap-2">
                                            @if (sample.media?.content?.cloudfrontUrl) {
                                                <img
                                                        [src]="sample.media.content.cloudfrontUrl"
                                                        alt="{{ sample.printedName }}"
                                                        class="w-6 h-6 object-cover rounded"
                                                />
                                            }
                                            <div class="flex flex-col">
                                                <span class="font-medium">{{ sample.printedName }}</span>
                                                <small class="text-gray-500">{{ sample.detailNames }}</small>
                                                <div class="flex items-center gap-2 mt-1">
                                                    <!-- ES2 key -->
                                                    <span class="text-xs text-gray-600">{{ sample.es2Key }}</span>
                                                    <!-- type tag -->
                                                    <p-tag
                                                            [value]="sample.type?.name"
                                                            class="text-xs"
                                                            severity="secondary"
                                                    ></p-tag>
                                                    <!-- category tag -->
                                                    <p-tag
                                                            [value]="sample.category?.name ?? 'No category'"
                                                            class="text-xs"
                                                            severity="secondary"
                                                    ></p-tag>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </ng-template>

                                <ng-template #item let-sample>
                                    <div class="flex items-center gap-2">
                                        @if (sample.media?.content?.cloudfrontUrl) {
                                            <img
                                                    [src]="sample.media.content.cloudfrontUrl"
                                                    alt="{{ sample.printedName }}"
                                                    class="w-6 h-6 object-cover rounded"
                                            />
                                        }
                                        <div class="flex flex-col">
                                            <span>{{ sample.printedName }}</span>
                                            <small class="text-gray-500">{{ sample.detailNames }}</small>

                                            <div class="flex items-center gap-2 mt-1">
                                                <span class="text-xs text-gray-600">{{ sample.es2Key }}</span>
                                                <p-tag
                                                        [value]="sample.type?.name"
                                                        class="text-xs"
                                                        severity="secondary"
                                                ></p-tag>
                                                <p-tag
                                                        [value]="sample.category?.name ?? 'No category'"
                                                        class="text-xs"
                                                        severity="secondary"
                                                ></p-tag>
                                            </div>
                                        </div>
                                    </div>
                                </ng-template>
                            </p-select>
                        </td>
                        <td class="min-w-52">
                            <p-select
                                    [(ngModel)]="discovery.selectedStudioId"
                                    [options]="studioOptions"
                                    appendTo="body"
                                    class="w-full"
                                    filter
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="Select Studio"
                            ></p-select>
                        </td>
                        <td>
                            <div class="flex gap-2 justify-end">
                                <p-button
                                        (click)="ignorePhysicalSampleDiscovery(discovery)"
                                        icon="pi pi-eye-slash"
                                        pTooltip="Ignore discovery"
                                        severity="secondary"
                                        text
                                ></p-button>
                                <p-button
                                        (click)="confirmPhysicalSampleDiscovery(discovery)"
                                        [disabled]="!discovery.selectedSampleId || !discovery.selectedStudioId"
                                        [loading]="discovery.confirming"
                                        icon="pi pi-check"
                                        label="Confirm"
                                ></p-button>
                            </div>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        }
    </div>
}

<div class="mb-6">
    <div class="flex justify-between mb-2">
        <div class="flex items-center gap-2">
            <p-ifta-label>
                <p-iconfield>
                    <p-inputicon class="pi pi-search"></p-inputicon>
                    <input
                            pInputText
                            id="physical-sample-search"
                            [(ngModel)]="this.search"
                            (input)="onSearchInputChange()"
                            autocomplete="off"
                    />
                </p-iconfield>
                <label for="physical-sample-search">Search Physical Sample</label>
            </p-ifta-label>
            <span class="relative inline-flex items-center gap-2">
        <p-button
                (click)="displayFilterDialog()"
                icon="pi pi-filter"
                label="Filter"
                severity="secondary"
                text
        ></p-button>
                @if (hasActiveFilters) {
                    <span
                            class="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500"
                    ></span>
                }

      </span>
            @if (activeFiltersText) {
                <small class="text-xs text-gray-500 italic">
                    Active filters: {{ activeFiltersText }}
                </small>
            }
        </div>
        <div class="flex gap-4 mb-2">
            <p-button
                    label="Add Physical Sample"
                    icon="pi pi-plus"
                    (click)="openCreateDialog()"
                    isProducer
            ></p-button>
        </div>
    </div>

    <div class="border border-gray-300 dark:border-gray-700 p-2 dark:p-0 mb-5 rounded-sm">
        <p-table
                [first]="this.currentPage * this.rows"
                [lazy]="true"
                dataKey="macAddress"
                [paginator]="true"
                [rows]="rows"
                [totalRecords]="this.totalRecords"
                [loading]="this.loading"
                [value]="this.physicalSamples"
                [rowsPerPageOptions]="[5, 10, 20]"
                (onLazyLoad)="this.onPageChange($event)"
        >
            <ng-template pTemplate="header">
                <tr>
                    <th pSortableColumn="macAddress">
                        MAC Address
                        <p-sortIcon field="macAddress"></p-sortIcon>
                    </th>
                    <th pSortableColumn="state">
                        State
                        <p-sortIcon field="state"></p-sortIcon>
                    </th>
                    <th pSortableColumn="firmwareVersion">
                        Firmware Version
                        <p-sortIcon field="firmwareVersion"></p-sortIcon>
                    </th>
                    <th pSortableColumn="battery">
                        Battery
                        <p-sortIcon field="battery"></p-sortIcon>
                    </th>
                    <th pSortableColumn="sample">
                        Sample
                        <p-sortIcon field="sample"></p-sortIcon>
                    </th>
                    <th pSortableColumn="ownerDealership">
                        Owner Dealership
                        <p-sortIcon field="ownerDealership"></p-sortIcon>
                    </th>
                    <th pSortableColumn="studio">
                        Studio
                        <p-sortIcon field="studio"></p-sortIcon>
                    </th>
                    <th></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-physicalSample>
                <tr>
                    <td>{{ physicalSample.macAddress }}</td>
                    <td>
                        <p-tag
                                [icon]="physicalSample.isLightOn === true ? 'pi pi-lightbulb'
                                    : physicalSample.state === 'ONLINE' ? 'pi pi-check-circle' 
                                    : physicalSample.state === 'OFFLINE' ? 'pi pi-times-circle' 
                                    : physicalSample.state === 'UNKNOWN' ? 'pi pi-question-circle'
                                    : 'pi pi-exclamation-triangle'"
                                [severity]="physicalSample.state === 'ONLINE' ? 'success'
                                    : physicalSample.state === 'OFFLINE' ? 'danger' 
                                    : physicalSample.state === 'UNKNOWN' ? 'secondary' 
                                    : 'warn'"
                                pTooltip="{{ physicalSample.state ? (physicalSample.state | enumLabel) : '' }}">
                        </p-tag>
                    </td>
                    <td>
                        <p-tag
                                [value]="physicalSample.firmwareVersion ? physicalSample.firmwareVersion : ''"
                                [icon]="!physicalSample.firmwareVersion ? 'pi pi-question-circle' : undefined"
                                pTooltip="{{ !physicalSample.firmwareVersion ? 'UNKNOWN' : '' }}">
                        </p-tag>
                    </td>
                    <td>
                        <p-tag
                                [value]="''"
                                [icon]="physicalSample.battery === 'FULL' ? 'pi pi-align-justify'
                                 : physicalSample.battery === 'HIGH' ? 'pi pi-bars'
                                 : physicalSample.battery === 'MEDIUM' ? 'pi pi-equals'
                                 : physicalSample.battery === 'LOW' ? 'pi pi-minus'
                                 : physicalSample.battery === 'CRITICAL' ? 'pi pi-exclamation-triangle'
                                 : physicalSample.battery === 'CHARGING' ? 'pi pi-bolt'
                                 : 'pi pi-question-circle'"
                                [severity]="physicalSample.battery === 'FULL' ? 'success'
                                    : physicalSample.battery === 'HIGH' ? 'success'
                                    : physicalSample.battery === 'MEDIUM' ? 'info'
                                    : physicalSample.battery === 'LOW' ? 'warn'
                                    : physicalSample.battery === 'CRITICAL' ? 'danger'
                                    : physicalSample.battery === 'CHARGING' ? 'info'
                                    : 'secondary'"
                                pTooltip="{{ physicalSample.battery ? (physicalSample.battery | enumLabel) : 'Unknown' }}">
                        </p-tag>
                    </td>
                    <td>
                        @if (physicalSample.sample) {
                            <div class="flex items-center gap-2">
                                <!-- Thumbnail -->
                                @if (physicalSample.sample.media?.content?.cloudfrontUrl) {
                                    <img
                                            [src]="physicalSample.sample.media.content.cloudfrontUrl"
                                            alt="{{ physicalSample.sample.printedName }}"
                                            class="w-6 h-6 object-cover rounded"
                                    />
                                }
                                <!-- Info -->
                                <div class="flex flex-col">
                                    <span class="font-medium">{{ physicalSample.sample.printedName }}</span>
                                    <small class="text-gray-500">{{ physicalSample.sample.detailNames }}</small>
                                    <div class="flex items-center gap-2 mt-1">
                                        <!-- ES2 key -->
                                        <span class="text-xs text-gray-600">{{ physicalSample.sample.es2Key }}</span>
                                        <!-- type tag -->
                                        <p-tag
                                                [value]="physicalSample.sample.type?.name"
                                                class="text-xs"
                                                severity="secondary"
                                        ></p-tag>
                                        <!-- category tag -->
                                        <p-tag
                                                [value]="physicalSample.sample.category?.name ?? 'No category'"
                                                class="text-xs"
                                                severity="secondary"
                                        ></p-tag>
                                    </div>
                                </div>
                            </div>
                        }
                    </td>
                    <td>
                        @if (physicalSample.ownerDealership) {
                            <div class="flex flex-col">
                                <span class="font-medium">{{ physicalSample.ownerDealership.name }}</span>
                                <div class="flex items-center gap-2 text-xs text-gray-500 mt-1">
                                    @if (physicalSample.ownerDealership.country?.name) {
                                        <span>
                      {{ physicalSample.ownerDealership.country.name }}
                    </span>
                                    }
                                    @if (physicalSample.ownerDealership.gssn) {
                                        <span>
                      · {{ physicalSample.ownerDealership.gssn }}
                    </span>
                                    }
                                </div>
                            </div>
                        }
                    </td>
                    <td>
                        @if (physicalSample.studio) {
                            <div class="flex flex-col">
                                <span class="font-medium">{{ physicalSample.studio.name }}</span>
                                <small class="text-gray-500">{{ physicalSample.studio.hostname }}</small>
                                <div class="mt-1">
                                    <p-tag
                                            [value]="physicalSample.studio.state"
                                        [severity]="
                                            physicalSample.studio.state === 'ONLINE' ? 'success' :
                                            physicalSample.studio.state === 'OFFLINE' ? 'danger' :
                                            'secondary'
                                        "
                                            class="text-xs"
                                    ></p-tag>
                                </div>
                            </div>
                        }
                    </td>
                    <td>
                        <div class="flex justify-end">
                            <p-button icon="pi pi-eye" text
                                      pTooltip="View physical sample"
                                      tooltipPosition="left"
                                      routerLink="/physical-samples/physical-sample/{{ physicalSample.id }}"></p-button>

                            <p-button icon="pi pi-pencil" text
                                      pTooltip="Edit physical sample"
                                      tooltipPosition="left"
                                      [hidden]="false"
                                      isProducer
                                      (click)="editPhysicalSample(physicalSample)"></p-button>

                            <p-button icon="pi pi-trash" text
                                      pTooltip="Delete physical sample"
                                      tooltipPosition="left"
                                      (click)="deletePhysicalSample(physicalSample)"
                                      [hidden]="false"
                                      isProducer
                                      severity="danger"></p-button>
                        </div>

                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td class="text-center text-gray-500 dark:text-gray-400" colspan="8">
                        No physical samples found.
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
</div>

<div class="flex gap-2 mb-6">
    <p-button
            label="Export Physical Samples"
            icon="pi pi-download"
            (click)="exportPhysicalSamples()"
            severity="secondary"
    ></p-button>
</div>

<!-- Create Physical Sample Dialog -->
<app-create-physical-sample-dialog
        [(visible)]="this.displayCreateDialog"
        (physicalSampleCreated)="reloadPhysicalSamples()"
></app-create-physical-sample-dialog>

<!-- Update Physical Sample Dialog -->
<app-update-physical-sample-dialog
        [(visible)]="this.displayUpdateDialog"
        [physicalSample]="this.selectedPhysicalSample"
        (physicalSampleUpdated)="reloadPhysicalSamples()"
></app-update-physical-sample-dialog>

<!-- Filter Physical Samples Dialog -->
<app-filter-physical-samples-dialog
        [(visible)]="filterDialogVisible"
        [stateOptions]="stateOptions"
        [firmwareVersionOptions]="firmwareVersionOptions"
        [ownerDealershipOptions]="ownerDealershipOptions"
        [studioOptions]="studioOptions"
        [sampleOptions]="sampleOptions"
        [activeFilters]="{
            state: selectedStates,
            firmwareVersions: selectedFirmwareVersions,
            ownerDealershipIds: selectedOwnerDealershipIds,
            studioIds: selectedStudioIds,
            sampleIds: selectedSampleIds,
            batteryStatuses: selectedBatteryStatuses
        }"
        (filtersApplied)="onFiltersApplied($event)">
</app-filter-physical-samples-dialog>
